import { DownloadOutlined, FileZipOutlined, WarningOutlined } from '@ant-design/icons';
import type { AccountingExportMissingDocument, AccountingExportPreview } from '@financiar/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert, App, Button, Card, Col, DatePicker, Empty, Row, Segmented, Select, Space, Statistic, Table, Typography, theme as antTheme } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { getApiError } from '../../api/client';
import { CardRow, MobileCardList } from '../../components/MobileCardList';
import { downloadBlob } from '../../lib/download';
import { DateText, MoneyText } from '../../lib/format';
import { useIsMobile } from '../../lib/responsive';
import { usePersistedState } from '../../lib/usePersistedState';
import { ExpenseKindTag } from '../cheltuieli/tags';
import { useFirmaCurenta } from '../firme/FirmaCurentaContext';
import { downloadContabilitateArchive, fetchContabilitatePreview } from './api';

const { Title, Text, Paragraph } = Typography;

type Quarter = 1 | 2 | 3 | 4;

/** Persisted period choice: a calendar quarter (the normal case) or a free range. */
type PeriodState =
  | { mode: 'quarter'; year: number; quarter: Quarter }
  | { mode: 'custom'; range: [string, string] | null };

const QUARTER_OPTIONS: { label: string; value: Quarter; hint: string }[] = [
  { label: 'T1', value: 1, hint: 'ianuarie – martie' },
  { label: 'T2', value: 2, hint: 'aprilie – iunie' },
  { label: 'T3', value: 3, hint: 'iulie – septembrie' },
  { label: 'T4', value: 4, hint: 'octombrie – decembrie' },
];

/** [from, to] of a calendar quarter as ISO dates. Snap to day 1 before
 * setting the month so a 31st can never overflow (same trick as
 * FacturiListPage's `periodRange`, no `quarterOfYear` plugin needed). */
function quarterRange(year: number, quarter: Quarter): [string, string] {
  const start = dayjs().year(year).startOf('year').month((quarter - 1) * 3).startOf('month');
  const end = start.add(2, 'month').endOf('month');
  return [start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD')];
}

/** The quarter that just closed — what one reports after a quarter ends. */
function previousQuarter(now: Dayjs): { year: number; quarter: Quarter } {
  const current = (Math.floor(now.month() / 3) + 1) as Quarter;
  if (current === 1) return { year: now.year() - 1, quarter: 4 };
  return { year: now.year(), quarter: (current - 1) as Quarter };
}

function resolvePeriod(state: PeriodState): [string, string] | null {
  if (state.mode === 'quarter') return quarterRange(state.year, state.quarter);
  return state.range;
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

function SummaryCard({
  title,
  value,
  subtitle,
  color,
  loading,
}: {
  title: string;
  value: React.ReactNode;
  subtitle: string;
  color?: string;
  loading: boolean;
}) {
  return (
    <Card loading={loading}>
      <Statistic title={title} value={0} valueStyle={color ? { color } : undefined} formatter={() => value} />
      <Text type="secondary" style={{ fontSize: 12 }}>
        {subtitle}
      </Text>
    </Card>
  );
}

export function ContabilitatePage() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { firmaId, firma, isLoading: isLoadingFirma } = useFirmaCurenta();
  const {
    token: { colorError, colorSuccess },
  } = antTheme.useToken();

  const [period, setPeriod] = usePersistedState<PeriodState>('contabilitate.period', {
    mode: 'quarter',
    ...previousQuarter(dayjs()),
  });
  const range = useMemo(() => resolvePeriod(period), [period]);
  const rangeDayjs = useMemo<[Dayjs, Dayjs] | null>(() => (range ? [dayjs(range[0]), dayjs(range[1])] : null), [range]);

  const currentYear = dayjs().year();
  const yearOptions = Array.from({ length: 6 }, (_, i) => ({ value: currentYear - i, label: String(currentYear - i) }));

  const params = firmaId && range ? { companyId: firmaId, dateFrom: range[0], dateTo: range[1] } : null;

  const previewQuery = useQuery({
    queryKey: ['accounting-export', 'preview', params],
    queryFn: () => fetchContabilitatePreview(params!),
    enabled: params !== null,
  });
  const preview: AccountingExportPreview | undefined = previewQuery.data;

  const download = useMutation({
    mutationFn: async () => {
      if (!params) throw new Error('no period');
      const { blob, filename } = await downloadContabilitateArchive(params);
      downloadBlob(blob, filename);
      return filename;
    },
    onSuccess: (filename) => message.success(`Arhiva ${filename} a fost descărcată.`),
    onError: (error) => message.error(getApiError(error)?.message ?? 'Arhiva nu a putut fi generată.'),
  });

  const nothingToExport = !!preview && preview.issuedInvoices.count === 0 && preview.expenses.count === 0;

  const missingColumns: ColumnsType<AccountingExportMissingDocument> = [
    { title: 'Dată', dataIndex: 'docDate', key: 'docDate', width: 110, render: (value: string) => <DateText value={value} /> },
    { title: 'Furnizor', key: 'supplier', render: (_, row) => row.supplierName ?? <Text type="secondary">—</Text> },
    { title: 'Nr. document', dataIndex: 'docNumber', key: 'docNumber', render: (value: string | null) => value ?? <Text type="secondary">—</Text> },
    { title: 'Tip', key: 'kind', width: 110, render: (_, row) => <ExpenseKindTag kind={row.kind} /> },
    { title: 'Descriere', dataIndex: 'description', key: 'description', ellipsis: true, render: (value: string | null) => value ?? '' },
    { title: 'Total (RON)', key: 'total', align: 'right', width: 130, render: (_, row) => <MoneyText value={row.totalRon} /> },
    {
      title: '',
      key: 'actions',
      width: 110,
      render: (_, row) => (
        <Button size="small" onClick={() => navigate(`/cheltuieli?id=${row.expenseId}`)}>
          Deschide
        </Button>
      ),
    },
  ];

  const periodLabel = rangeDayjs ? `${rangeDayjs[0].format('DD.MM.YYYY')} – ${rangeDayjs[1].format('DD.MM.YYYY')}` : 'alege perioada';

  return (
    <div>
      <Title level={3} style={{ marginBottom: 4 }}>
        Contabilitate
      </Title>
      <Text type="secondary">{firma?.name ?? '—'}</Text>

      {!firmaId && !isLoadingFirma ? (
        <Empty description="Selectează sau adaugă mai întâi o firmă." style={{ marginTop: 48 }} />
      ) : (
        <>
          <Card style={{ marginTop: 24, marginBottom: 16 }}>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Space wrap size={[12, 12]}>
                <Segmented
                  value={period.mode}
                  options={[
                    { label: 'Trimestru', value: 'quarter' },
                    { label: 'Perioadă personalizată', value: 'custom' },
                  ]}
                  onChange={(mode) =>
                    setPeriod(
                      mode === 'quarter'
                        ? { mode: 'quarter', ...previousQuarter(dayjs()) }
                        : { mode: 'custom', range: range ?? quarterRange(currentYear, 1) },
                    )
                  }
                />
                {period.mode === 'quarter' ? (
                  <>
                    <Select
                      value={period.year}
                      options={yearOptions}
                      style={{ width: 100 }}
                      onChange={(year) => setPeriod({ ...period, year })}
                    />
                    <Segmented
                      value={period.quarter}
                      options={QUARTER_OPTIONS.map((q) => ({ label: q.label, value: q.value, title: q.hint }))}
                      onChange={(quarter) => setPeriod({ ...period, quarter: quarter as Quarter })}
                    />
                  </>
                ) : (
                  <DatePicker.RangePicker
                    value={rangeDayjs}
                    format="DD.MM.YYYY"
                    placeholder={['De la', 'Până la']}
                    allowClear={false}
                    style={isMobile ? { width: '100%' } : undefined}
                    onChange={(value) =>
                      setPeriod({
                        mode: 'custom',
                        range: value && value[0] && value[1] ? [value[0].format('YYYY-MM-DD'), value[1].format('YYYY-MM-DD')] : null,
                      })
                    }
                  />
                )}
              </Space>
              <Text type="secondary">
                Perioada raportată: <Text strong>{periodLabel}</Text>
                {preview?.period.quarterLabel ? ` (trimestrul ${preview.period.quarterLabel.slice(6)}, ${preview.period.quarterLabel.slice(0, 4)})` : ''}
              </Text>
            </Space>
          </Card>

          {previewQuery.isError ? (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message={getApiError(previewQuery.error)?.message ?? 'Rezumatul nu a putut fi încărcat.'}
            />
          ) : null}

          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={12} xl={6}>
              <SummaryCard
                title="FACTURI EMISE"
                value={<MoneyText value={preview?.issuedInvoices.total ?? '0.00'} />}
                subtitle={
                  preview
                    ? `${plural(preview.issuedInvoices.count, 'factură', 'facturi')}${preview.issuedInvoices.stornoCount ? ` • ${preview.issuedInvoices.stornoCount} storno` : ''}`
                    : ''
                }
                loading={previewQuery.isLoading}
              />
            </Col>
            <Col xs={12} xl={6}>
              <SummaryCard
                title="CHELTUIELI"
                value={<MoneyText value={preview?.expenses.totalRon ?? '0.00'} />}
                subtitle={
                  preview ? `${plural(preview.expenses.count, 'cheltuială', 'cheltuieli')} • ${preview.expenses.efacturaCount} e-Factura` : ''
                }
                loading={previewQuery.isLoading}
              />
            </Col>
            <Col xs={12} xl={6}>
              <SummaryCard
                title="DOCUMENTE ATAȘATE"
                value={<span>{preview?.documents.fileCount ?? 0}</span>}
                subtitle={preview ? `${plural(preview.documents.expenseCount, 'cheltuială', 'cheltuieli')} • ~${formatBytes(preview.documents.totalBytes)}` : ''}
                color={colorSuccess}
                loading={previewQuery.isLoading}
              />
            </Col>
            <Col xs={12} xl={6}>
              <SummaryCard
                title="DOCUMENTE LIPSĂ"
                value={<span>{preview?.missingDocuments.length ?? 0}</span>}
                subtitle={preview && preview.missingDocuments.length > 0 ? 'de completat înainte de trimitere' : 'toate cheltuielile au document'}
                color={preview && preview.missingDocuments.length > 0 ? colorError : undefined}
                loading={previewQuery.isLoading}
              />
            </Col>
          </Row>

          {preview && preview.warnings.length > 0 ? (
            <Alert
              type="warning"
              showIcon
              icon={<WarningOutlined />}
              style={{ marginBottom: 16 }}
              message="De verificat"
              description={
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {preview.warnings.map((w) => (
                    <li key={w.code}>{w.message}</li>
                  ))}
                </ul>
              }
            />
          ) : null}

          {preview && preview.missingDocuments.length > 0 ? (
            <Card title="Documente lipsă" style={{ marginBottom: 16 }}>
              <Paragraph type="secondary">
                Aceste cheltuieli nu au nici e-Factura din SPV, nici un document atașat. Adaugă documentele înainte de a trimite arhiva către
                contabilitate.
              </Paragraph>
              {isMobile ? (
                <MobileCardList<AccountingExportMissingDocument>
                  dataSource={preview.missingDocuments}
                  rowKey={(row) => row.expenseId}
                  onCardClick={(row) => navigate(`/cheltuieli?id=${row.expenseId}`)}
                  renderCard={(row) => (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                        <Text strong>{row.supplierName ?? row.description ?? 'Cheltuială'}</Text>
                        <ExpenseKindTag kind={row.kind} />
                      </div>
                      <CardRow label="Dată">
                        <DateText value={row.docDate} />
                      </CardRow>
                      <CardRow label="Nr. document">{row.docNumber ?? '—'}</CardRow>
                      <CardRow label="Total (RON)">
                        <MoneyText value={row.totalRon} />
                      </CardRow>
                    </>
                  )}
                />
              ) : (
                <Table<AccountingExportMissingDocument>
                  rowKey="expenseId"
                  size="small"
                  pagination={false}
                  dataSource={preview.missingDocuments}
                  columns={missingColumns}
                  // Seven columns do not fit a narrow desktop/tablet width — scroll
                  // horizontally instead of letting AntD crush the cells.
                  scroll={{ x: 820 }}
                />
              )}
            </Card>
          ) : null}

          <Card>
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <Button
                type="primary"
                size="large"
                icon={download.isPending ? <FileZipOutlined spin /> : <DownloadOutlined />}
                loading={download.isPending}
                disabled={!params || previewQuery.isLoading || previewQuery.isError || nothingToExport}
                onClick={() => download.mutate()}
                block={isMobile}
              >
                {download.isPending ? 'Se generează arhiva…' : 'Generează și descarcă'}
              </Button>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Arhiva conține fișierele SAGA de ieșiri și intrări (<Text code>F_CUI_iesiri_data.xml</Text>,{' '}
                <Text code>F_CUI_intrari_data.xml</Text> — numele e cel pe care îl caută SAGA la import, nu le redenumi), PDF-urile
                facturilor emise, documentele cheltuielilor fără e-Factura și <Text code>rezumat.xlsx</Text>. Prima generare a unei perioade poate dura
                câteva minute (se randează PDF-urile); dacă expiră, reîncearcă — PDF-urile deja generate rămân salvate și a doua
                descărcare e rapidă.
              </Text>
              {nothingToExport ? <Text type="secondary">Nu există facturi sau cheltuieli în perioada aleasă.</Text> : null}
            </Space>
          </Card>
        </>
      )}
    </div>
  );
}
